import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Invoice,
  InvoiceLine,
  InvoiceListResponse,
  InvoiceListItem,
  ApiResponse,
} from '@/api/types'

/**
 * Invoice management service (real ERP APIs)
 */
export class InvoicesService {
  /**
   * Get paginated list of invoices for a branch
   */
  async getInvoices(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<InvoiceListResponse> {
    const url = endpoints.invoices.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<InvoiceListResponse> | InvoiceListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as InvoiceListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as InvoiceListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as InvoiceListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Map API response to Invoice interface
   * Handles field name differences (invoiceNo -> invoiceNumber, refNo -> refNumber, etc.)
   */
  private mapInvoiceData(invoiceData: any, branchId: number): Invoice {
    return {
      invoiceId: invoiceData.invoiceId,
      seq: invoiceData.seq,
      invoiceDate: invoiceData.invoiceDate || '',
      invoiceNumber: invoiceData.invoiceNo || invoiceData.invoiceNumber || '',
      invoiceNo: invoiceData.invoiceNo,
      customerId: invoiceData.customerId || 0,
      customerName: invoiceData.customerName,
      dueDate: invoiceData.dueDate || '',
      refNo: invoiceData.refNo,
      refNumber: invoiceData.refNo || invoiceData.refNumber || '',
      currency: invoiceData.currency || 0,
      conversionRate: invoiceData.conversionRate || 1,
      taxRateId: invoiceData.taxRateId || null,
      uom: invoiceData.uom,
      subTotal: invoiceData.subTotal || 0,
      totalTax: invoiceData.totalTax || 0,
      discountPer: invoiceData.discountPer,
      totalDiscount: invoiceData.totalDiscount || 0,
      total: invoiceData.total || 0,
      note: invoiceData.note || '',
      isPosted: invoiceData.isPosted,
      postedDate: invoiceData.postedDate,
      postedBy: invoiceData.postedBy,
      glBatchId: invoiceData.glBatchId,
      companyId: invoiceData.companyId,
      branchId: invoiceData.branchId || branchId,
      portalId: invoiceData.portalId,
      createdDate: invoiceData.createdDate,
      createdBy: invoiceData.createdBy,
      modifiedDate: invoiceData.modifiedDate,
      modifiedBy: invoiceData.modifiedBy,
      destinationAccount: invoiceData.destinationAccount,
      recipientAccount: invoiceData.recipientAccount,
      projectId: invoiceData.projectId || 0,
      paymentTermId: invoiceData.paymentTermId || 0,
      retentionPercent: invoiceData.retentionPercent,
      totalRetentionAmount: invoiceData.totalRetentionAmount,
      amountType: invoiceData.amountType,
      category: invoiceData.category || 0,
      bankId: invoiceData.bankId,
      isBranchInvoice: invoiceData.isBranchInvoice,
      payStatus: invoiceData.payStatus,
      paidAmount: invoiceData.paidAmount,
      remainingAmount: invoiceData.remainingAmount,
      encodedInvoice: invoiceData.encodedInvoice,
      invStatus: invoiceData.invStatus,
      invoiceHash: invoiceData.invoiceHash,
      isPhase2: invoiceData.isPhase2,
      qrCode: invoiceData.qrCode,
      signedXml: invoiceData.signedXml,
      supplyDate: invoiceData.supplyDate || '',
      poNumber: invoiceData.poNumber || '',
    } as Invoice
  }

  /**
   * Get invoice by ID (for edit)
   */
  async getInvoiceById(branchId: number, invoiceId: number): Promise<Invoice> {
    const url = endpoints.invoices.getById(invoiceId, branchId)
    const response = await apiClient.get<ApiResponse<Invoice> | Invoice | any>(url)
    
    let invoiceData: any
    
    if ('data' in response.data && 'success' in response.data) {
      invoiceData = response.data.data
    } else if ('data' in response.data) {
      invoiceData = response.data.data
    } else {
      // Handle direct response (API might return the invoice directly)
      invoiceData = response.data
    }
    
    // Map API response fields to Invoice interface
    // API returns: invoiceNo, refNo, etc. - map to Invoice interface fields
    return this.mapInvoiceData(invoiceData, branchId)
  }

  /**
   * Get invoice lines (branch level template)
   */
  async getInvoiceLines(branchId: number): Promise<InvoiceLine[]> {
    const url = endpoints.invoices.getLines(branchId)
    const response = await apiClient.get<ApiResponse<InvoiceLine[]> | InvoiceLine[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get invoice lines by invoice ID (for edit)
   */
  async getInvoiceLinesById(branchId: number, invoiceId: number): Promise<InvoiceLine[]> {
    const url = endpoints.invoices.getLinesById(invoiceId, branchId)
    console.log('🟢 [InvoicesService] Loading invoice lines:', { url, invoiceId, branchId })
    const response = await apiClient.get<ApiResponse<InvoiceLine[]> | InvoiceLine[]>(url)
    console.log('🟢 [InvoicesService] Invoice lines response:', response.data)
    
    const rawList: any[] = Array.isArray(response.data)
      ? response.data
      : 'data' in (response.data as any) && Array.isArray((response.data as any).data)
        ? ((response.data as any).data as any[])
        : []

    console.log('🟢 [InvoicesService] Normalizing invoice lines:', rawList.length)

    return rawList.map((row: any): InvoiceLine => {
      const lineId = row.lineId ?? row.LineId ?? row.id ?? row.Id ?? 0
      const invId = row.invoiceId ?? row.InvoiceId ?? row.invoiceID ?? row.InvoiceID ?? invoiceId
      const itemId = row.itemId ?? row.ItemId ?? 0
      const description = row.description ?? row.Description ?? ''
      const qty = Number(row.qty ?? row.Qty ?? 0) || 0
      const unitPrice = Number(row.unitPrice ?? row.UnitPrice ?? 0) || 0
      const itemPrice = Number(row.itemPrice ?? row.ItemPrice ?? 0) || 0
      const discountPercent = Number(row.discountPercent ?? row.DiscountPercent ?? 0) || 0
      const discountAmount = Number(row.discountAmount ?? row.DiscountAmount ?? 0) || 0
      const taxableAmount = Number(row.taxableAmount ?? row.TaxableAmount ?? 0) || 0
      const taxRateIdRaw = row.taxRateId ?? row.TaxRateId ?? 0
      const taxRateId = Number(taxRateIdRaw) || 0
      const taxRate = Number(row.taxRate ?? row.TaxRate ?? row.taxPercent ?? row.TaxPercent ?? 0) || 0
      const taxPercent = Number(row.taxPercent ?? row.TaxPercent ?? taxRate) || 0
      const taxAmount = Number(row.taxAmount ?? row.TaxAmount ?? 0) || 0
      const lineAmount = Number(row.lineAmount ?? row.LineAmount ?? 0) || 0
      const uom = row.uom ?? row.Uom ?? row.UOM
      const companyId = row.companyId ?? row.CompanyId
      const branchIdLine = row.branchId ?? row.BranchId
      const isDeleted = row.isDeleted ?? row.IsDeleted ?? 'false'

      return {
        lineId,
        invoiceId: Number(invId) || invoiceId,
        itemId: Number(itemId) || 0,
        description: String(description ?? ''),
        qty,
        unitPrice,
        itemPrice,
        discountPercent,
        discountAmount,
        taxableAmount,
        taxRateId: taxRateId || undefined,
        taxRate,
        taxAmount,
        lineAmount,
        uom: uom != null ? Number(uom) : undefined,
        companyId: companyId != null ? Number(companyId) : undefined,
        branchId: branchIdLine != null ? Number(branchIdLine) : undefined,
        taxPercent,
        isDeleted,
      }
    })
  }

  /**
   * Insert invoice
   */
  async insertInvoice(invoice: Invoice): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice> | Invoice>(endpoints.invoices.insert, invoice)
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<Invoice>).data
    }
    return response.data as Invoice
  }

  /**
   * Update invoice
   */
  async updateInvoice(invoice: Invoice): Promise<Invoice> {
    const response = await apiClient.put<ApiResponse<Invoice> | Invoice>(endpoints.invoices.update, invoice)
    if ('data' in response.data && 'success' in response.data) {
      return (response.data as ApiResponse<Invoice>).data
    }
    return response.data as Invoice
  }

  /**
   * Post invoice (mark as posted)
   */
  async postInvoice(id: number): Promise<ApiResponse | { success?: boolean; message?: string }> {
    const url = endpoints.invoices.postData(id)
    const response = await apiClient.post<ApiResponse | { success?: boolean; message?: string }>(url)
    return response.data
  }
}

export const invoicesService = new InvoicesService()

